import { useState, useEffect, useCallback } from "react";
import { settingsApi } from "../api/settings";
import { suppliesApi, type SupplyInventory } from "../api/supplies";
import "./Settings.css";

type Status = "loading" | "idle" | "saving" | "saved" | "error";

export default function Settings() {
  const [homeAddress, setHomeAddress] = useState("");
  const [status, setStatus] = useState<Status>("loading");
  const [errorMsg, setErrorMsg] = useState("");

  // Bag & Card state
  const [bags, setBags] = useState<SupplyInventory | null>(null);
  const [cards, setCards] = useState<SupplyInventory | null>(null);
  const [restockType, setRestockType] = useState<"bags" | "cards" | null>(null);
  const [restockQty, setRestockQty] = useState("");
  const [restockCost, setRestockCost] = useState("");
  const [supplySaving, setSupplySaving] = useState(false);

  const loadSupplies = useCallback(async () => {
    const [bRes, cRes] = await Promise.all([suppliesApi.getBags(), suppliesApi.getCards()]);
    if (bRes.data) setBags(bRes.data);
    if (cRes.data) setCards(cRes.data);
  }, []);

  useEffect(() => {
    settingsApi.get().then((result) => {
      if (result.error || !result.data) {
        setStatus("error");
        setErrorMsg(result.error ?? "No data received");
        return;
      }
      setHomeAddress(result.data.homeAddress ?? "");
      setStatus("idle");
    });
    loadSupplies();
  }, [loadSupplies]);

  const handleSave = useCallback(async () => {
    setStatus("saving");
    setErrorMsg("");
    const result = await settingsApi.update({ homeAddress });
    if (result.error) {
      setStatus("error");
      setErrorMsg(result.error);
      return;
    }
    setStatus("saved");
    setTimeout(() => setStatus("idle"), 2000);
  }, [homeAddress]);

  const handleSupplyRestock = async () => {
    if (!restockType) return;
    setSupplySaving(true);
    const payload = { quantity: Number(restockQty), totalCost: Number(restockCost) };
    const result = restockType === "bags"
      ? await suppliesApi.restockBags(payload)
      : await suppliesApi.restockCards(payload);
    if (result.error) {
      setErrorMsg(result.error);
    } else {
      setRestockType(null);
      setRestockQty("");
      setRestockCost("");
      await loadSupplies();
    }
    setSupplySaving(false);
  };

  const isLoading = status === "loading";

  return (
    <div>
      <div className="screen-header">
        <h1>Settings</h1>
        <p>Address, pricing, and costs</p>
      </div>

      {isLoading ? (
        <p className="settings-status">Cargando configuración…</p>
      ) : (
        <form
          className="settings-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          {/* ─── Home Address ────────────────────────── */}
          <label className="settings-field" htmlFor="homeAddress">
            <span className="settings-field__label">
              <span className="material-symbols-rounded">home</span>
              {" "}Dirección de origen (para Uber)
            </span>
            <input
              id="homeAddress" type="text" className="settings-field__input"
              value={homeAddress} onChange={(e) => setHomeAddress(e.target.value)}
              placeholder="Ej: Av. Insurgentes 123, CDMX" disabled={status === "saving"}
            />
          </label>

          {/* ─── Save Button ─────────────────────────── */}
          <button type="submit" className="settings-btn" disabled={status === "saving"}>
            {status === "saving" ? "Guardando…" : (
              <><span className="material-symbols-rounded">save</span> Guardar configuración</>
            )}
          </button>

          {/* ─── Status Messages ─────────────────────── */}
          {status === "saved" && <p className="settings-msg settings-msg--success">Configuración guardada</p>}
          {status === "error" && <p className="settings-msg settings-msg--error">Error: {errorMsg}</p>}
        </form>
      )}

      {/* ─── Bag & Card Supplies ────────────────────── */}
      <div className="supplies-section">
        <h3 className="supplies-title">
          <span className="material-symbols-rounded">package_2</span> Bolsas y Tarjetas PVC
        </h3>

        {/* Bags */}
        <div className="supply-card">
          <div className="supply-card__info">
            <span className="supply-card__name">
              <span className="material-symbols-rounded">shopping_bag</span> Bolsas
            </span>
            <span className="supply-card__stock">Stock: {bags?.stockQuantity ?? 0}</span>
            <span className="supply-card__cost">
              Costo prom: ${bags?.weightedAvgCost?.toFixed(2) ?? "0.00"}
            </span>
          </div>
          <button
            className="supply-card__btn"
            onClick={() => { setRestockType("bags"); setRestockQty(""); setRestockCost(""); }}
          >
            + Restock
          </button>
        </div>

        {/* Cards */}
        <div className="supply-card">
          <div className="supply-card__info">
            <span className="supply-card__name">
              <span className="material-symbols-rounded">credit_card</span> Tarjetas PVC
            </span>
            <span className="supply-card__stock">Stock: {cards?.stockQuantity ?? 0}</span>
            <span className="supply-card__cost">
              Costo prom: ${cards?.weightedAvgCost?.toFixed(2) ?? "0.00"}
            </span>
          </div>
          <button
            className="supply-card__btn"
            onClick={() => { setRestockType("cards"); setRestockQty(""); setRestockCost(""); }}
          >
            + Restock
          </button>
        </div>

        {/* Restock Form */}
        {restockType && (
          <form
            className="inv-form"
            style={{ marginTop: "0.75rem" }}
            onSubmit={(e) => { e.preventDefault(); handleSupplyRestock(); }}
          >
            <h3 className="inv-form__title">
              Restock: {restockType === "bags" ? "Bolsas" : "Tarjetas PVC"}
            </h3>
            <div className="inv-form__prices">
              <label>
                <span>Cantidad</span>
                <input
                  className="inv-input" type="number" placeholder="Cantidad"
                  value={restockQty} onChange={(e) => setRestockQty(e.target.value)}
                  min={1} disabled={supplySaving} autoFocus
                />
              </label>
              <label>
                <span>Costo total del lote ($)</span>
                <input
                  className="inv-input" type="number" placeholder="Costo total"
                  value={restockCost} onChange={(e) => setRestockCost(e.target.value)}
                  min={0} step="0.01" disabled={supplySaving}
                />
              </label>
            </div>
            <div className="inv-form__actions">
              <button type="submit" className="inv-btn inv-btn--primary" disabled={supplySaving}>
                {supplySaving ? "Guardando…" : (
                  <><span className="material-symbols-rounded">save</span> Registrar restock</>
                )}
              </button>
              <button type="button" className="inv-btn inv-btn--ghost" onClick={() => setRestockType(null)}>
                Cancelar
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
